"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resetPasswordResultSchema = exports.RESET_OUTCOMES = exports.resetPasswordRequestSchema = exports.passwordResetPreviewSchema = exports.PASSWORD_RESET_STATES = exports.forgotPasswordRequestSchema = exports.PASSWORD_RESET_ROUTE = exports.PASSWORD_RESET_PATHS = exports.PASSWORD_RESET_HOURS = void 0;
exports.passwordResetLink = passwordResetLink;
exports.maskEmail = maskEmail;
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Forgotten passwords. The request, the link, and setting a new one.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS IS OUR OWN TOKEN, NOT GOTRUE'S RECOVERY FLOW, AND THE CHOICE IS DELIBERATE.**
 * GoTrue can mail a recovery link itself — `/auth/v1/recover` — and every part of that is wrong for
 * this product:
 *
 *  - **it mails from GoTrue's own templates**, in one language, bypassing `mail/copy.ts` and the
 *    rule that an email is written in the RECIPIENT's language (C17's notification centre argues it:
 *    *"a screen can be re-rendered in the reader's language; an email cannot"*);
 *  - **it bypasses the job queue**, so the send happens inside the request — `0024`'s header:
 *    *"an SMTP handshake inside one makes the request as failable as the mail server"*;
 *  - **its link carries a `#access_token` fragment** and lands the browser in a half-authenticated
 *    state, which is a second, parallel notion of "signed in" for one screen to understand.
 *
 * So this mirrors the **invitation**, which had all the same problems and solved them: a 256-bit
 * token, stored as a hash, with an expiry, mailed through the queue, redeemed against a
 * `security definer` function by somebody holding no session at all.
 *
 * ⚠️ **`AUTH_PATHS.resetPassword` is NOT this path, and conflating them would be a bug.** That
 * constant is GoTrue's redirect target — allow-listed in `config.toml` for the flow described above,
 * which we do not use. Its link shape is a fragment, ours is a path parameter; one screen cannot
 * serve both contracts, and a screen at that address would receive GoTrue's shape from nowhere.
 */
/**
 * How long a reset link works.
 *
 * ⚠️ **Hours, where an invitation gets fourteen days, and the asymmetry is the point.** An
 * invitation waits for somebody to come back from holiday and start a new job; a reset is requested
 * by somebody sitting at the keyboard right now, locked out. A link that works for a fortnight is a
 * fortnight of *"click here to take over this account"* sitting in a mailbox — and unlike an
 * invitation, the account it opens already exists and already has data in it.
 */
exports.PASSWORD_RESET_HOURS = 2;
/**
 * The address bar, for both halves of the flow.
 *
 * ⚠️⚠️ **ONE SPELLING, CONSUMED BY THE API AND BY `routes.tsx`, BECAUSE `hard-won.md` WP34-2 IS
 * EXACTLY THIS SHAPE.** There, the API composed `/auth/verify-email`, the app served `/auth/verify`
 * and `config.toml` allow-listed a third — every confirmation link was broken and each file was
 * internally consistent, so nothing was red anywhere. The invitation link still has two hand-written
 * spellings (`invitation.service.ts` builds one, `routes.tsx` declares the other); this one does
 * not, and that is the only difference worth arguing about here.
 */
exports.PASSWORD_RESET_PATHS = {
    /** Where somebody asks for a link. */
    request: '/forgot-password',
    /** The base of the link in the email. The token is one more segment. */
    redeem: '/reset-password',
};
/** The route pattern `routes.tsx` registers. Derived, so the two cannot disagree. */
exports.PASSWORD_RESET_ROUTE = `${exports.PASSWORD_RESET_PATHS.redeem}/:token`;
/** The absolute link that goes in the email. */
function passwordResetLink(origin, token) {
    /* `new URL`, for `authRedirectUrl`'s reason: `${origin}${path}` doubles the slash the moment
     * somebody sets `APP_ORIGIN` with a trailing one, and the result looks correct in a log. */
    return new URL(`${exports.PASSWORD_RESET_PATHS.redeem}/${encodeURIComponent(token)}`, origin).toString();
}
/**
 * Asking for a link.
 *
 * ⚠️⚠️ **THERE IS NO RESPONSE BODY WORTH BRANCHING ON, AND THAT IS THE SECURITY PROPERTY.** The
 * endpoint answers `202` whether or not the address belongs to an account — see
 * `password-reset.service.ts`. `auth.controller.ts`'s `signUp` already refuses to leak existence
 * (*"GoTrue leaks existence with a 422 and this is where that stops"*), and a forgotten-password
 * form is the easiest existence oracle in any product: unauthenticated, unrate-limited by habit, and
 * the answer is the whole question.
 */
exports.forgotPasswordRequestSchema = zod_1.z.object({
    email: zod_1.z.email().min(3).max(320),
});
/**
 * Why a reset link cannot be used, or `pending`.
 *
 * ⚠️ **One state rather than three booleans, and the ORDER is part of the contract** — the
 * invitation's `INVITATION_STATES` argues it, and the client renders one sentence per state rather
 * than combining flags two different ways in two places.
 */
exports.PASSWORD_RESET_STATES = ['pending', 'used', 'expired'];
/**
 * What the link's page shows before anything is typed.
 *
 * ⚠️⚠️ **THE ADDRESS IS MASKED, AND THE INVITATION'S PAGE SHOWS ITS ONE IN FULL — THE DIFFERENCE IS
 * WHO IS HOLDING THE LINK.** An invitation is addressed to somebody who does not have an account
 * yet, and the page must state which address to sign in with or the refusal that follows is a
 * mystery. A reset link goes to an address its owner already knows; printing it in full turns a
 * forwarded or shoulder-surfed link into a confirmed *"this person has an account here"*, which is
 * the same existence leak the request endpoint just refused to make. The mask is enough for
 * *"yes, that is my work address"* and no use to anybody else.
 */
exports.passwordResetPreviewSchema = zod_1.z.object({
    /** `a•••@loginet.hu`. Masked by the server, so no client can decide to render it whole. */
    maskedEmail: zod_1.z.string(),
    expiresAt: zod_1.z.string(),
    state: zod_1.z.enum(exports.PASSWORD_RESET_STATES),
});
/**
 * Setting the new one.
 *
 * ⚠️ **No `min` on the password, matching `auth.controller.ts`'s own reasoning:** the policy lives in
 * `config.toml`, GoTrue owns it, and a length here would be a second opinion that drifts.
 * `PASSWORD_MIN_LENGTH` is for drawing the checklist. The service checks the policy anyway before it
 * calls GoTrue's ADMIN endpoint — which enforces nothing at all, as `invitation.service.ts` found
 * the hard way.
 */
exports.resetPasswordRequestSchema = zod_1.z.object({
    password: zod_1.z.string().min(1).max(200),
});
/**
 * What happened when the new password was submitted.
 *
 * ⚠️ **`unknown` covers a bad token AND a token for an account that has since gone**, deliberately:
 * rule 5 says a record somebody may not see is `not_found` rather than a taxonomy of nos, and the
 * distinction here would say *"that token was real once"*.
 */
exports.RESET_OUTCOMES = ['reset', 'expired', 'used', 'weak_password', 'unknown'];
exports.resetPasswordResultSchema = zod_1.z.object({
    outcome: zod_1.z.enum(exports.RESET_OUTCOMES),
    /** GoTrue's own reasons, for `weak_password`. Rendered against the catalogue's rule labels. */
    reasons: zod_1.z.array(zod_1.z.string()),
});
/**
 * `anna.kovacs@loginet.hu` → `a••••••••••@loginet.hu`.
 *
 * ⚠️ **In `shared` and pure, so the API and its test agree by construction rather than by two
 * regexes that look alike.** The masking happens on the SERVER — this is exported for the test and
 * for anywhere a client would otherwise be tempted to do its own.
 *
 * ⚠️ **The dot count does not track the real length.** A mask that is exactly as long as the address
 * tells an attacker the length of it, which is a small piece of the same thing the mask exists to
 * withhold.
 */
function maskEmail(email) {
    const at = email.lastIndexOf('@');
    if (at <= 0)
        return '•••';
    const first = email.slice(0, 1);
    const domain = email.slice(at);
    return `${first}••••${domain}`;
}
//# sourceMappingURL=password-reset.js.map